import { useRef, useState, useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { Button, Dropdown, DropdownContent, IconButton } from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import { tooltipText } from 'shared/constants/tooltipText'
import { DeviceSettingsContent } from 'entities/DeviceSettings'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { useContactsProfileCardContext } from 'widgets/ContactsDetails/ui/ContactsProfileCard/context'
import { contactsDetailsGlobalStore } from 'widgets/ContactsDetails/store'
import styles from './styles.module.scss'

export const CallButtonAction = observer(() => {
  const {
    callStore,
    featureFlagsStore,
    inboxesStore,
    remindersStore,
    usersStore,
    organizationStore,
  } = useEntitiesContext()
  const [isDropdownOpen, setDropdownOpen] = useState(false)
  const buttonRef = useRef<HTMLButtonElement | null>(null)
  const store = useContactsProfileCardContext()
  const {
    conversation,
    disabledNewConversationCall,
    isVariantCreateContactModal,
    handleConversationCall,
    creatingConversation,
    isMultipleContacts,
    contact,
    inbox,
    config,
  } = store

  const inteliquentCalling = featureFlagsStore.inteliquent_calling
  const canManageInboxCalling = featureFlagsStore.settings_inboxes_manage_inbox_calling_v2
  const user = usersStore.user
  const isAirCall = conversation?.is_aircall
  const isCallViaAirCall = Boolean(
    organizationStore.organization_call?.call_via_aircall?.is_active &&
      featureFlagsStore.call_via_aircall
  )
  const isOutOfMoney = remindersStore.creditsStore.isOutOfMoney

  const isCallOutboundDisabled = isAirCall ? false : !inbox?.isNumberOutboundCalls
  const isPFTInbox = Boolean(inbox?.isPFTInbox)
  const isRCS = Boolean(inbox?.isRCS)
  const isUserViewOnly = Boolean(user?.isViewOnlyRole)
  const isContactBlocked = contact?.is_blocked === true
  const isNumberNotVerifiedCallerId = inbox?.isNumberNotVerifiedCallerId
  const callerIdFailedReason = inbox?.type === 'inbox' && inbox?.callerIdFailedReason

  const isCallDisabled =
    isUserViewOnly ||
    isContactBlocked ||
    isMultipleContacts ||
    disabledNewConversationCall ||
    callStore.loading ||
    callStore.isBusy ||
    (isPFTInbox && !inteliquentCalling) ||
    (isOutOfMoney && !isAirCall) ||
    isNumberNotVerifiedCallerId ||
    isRCS

  const isCallButtonDisabled =
    callStore.isBusy ||
    (isVariantCreateContactModal
      ? disabledNewConversationCall
      : isCallDisabled || isCallOutboundDisabled)

  const isCallDropdownShow = isVariantCreateContactModal
    ? !callStore.disabled
    : !callStore.disabled && !isAirCall && !isCallViaAirCall

  const titleContent = (
    <div className={styles.wrap}>
      Call
      {isCallDropdownShow && (
        <>
          <span className={styles.divider} />
          <Dropdown
            ariaLabel={'ContactCallButton'}
            show={isDropdownOpen}
            PopperProps={{
              anchorEl: buttonRef.current,
              onClick: (event) => {
                event.stopPropagation()
                event.preventDefault()
              },
            }}
            triggerComponent={(isExpanded) => (
              <IconButton
                className={styles.icon}
                tag='span'
                variant={'icon'}
                color={'transparent'}
                size={'origin_icon'}
                onClick={() => setDropdownOpen(!isExpanded)}
                iconProps={{
                  icon: isExpanded ? 'chevronUp' : 'chevronDown',
                  fontSize: 14,
                }}
                ariaLabel='ContactCallButton_setDropdownOpen'
              />
            )}
            placement={'bottom-end'}
            width={246}
            margin={8}
            customComponent={(_, dropdownId) => (
              <DropdownContent>
                <DeviceSettingsContent
                  parentsDropdownId={[dropdownId]}
                  deviceSettingsStore={callStore.deviceSettingsStore}
                  testOutputDevice={callStore.testOutputDevice}
                />
              </DropdownContent>
            )}
            onToggle={(value) => {
              setDropdownOpen(value)
            }}
          />
        </>
      )}
    </div>
  )

  const getTooltipLabel = () => {
    if (isRCS) return 'RCS does not support calls'
    if (isPFTInbox && !inteliquentCalling)
      return tooltipText.disabledCall(inboxesStore.nonPFTInboxNumberType)
    if (isOutOfMoney && !isAirCall) return 'Your organization is out of message credits'
    if (isMultipleContacts) return 'Calling is disabled for group chats'
    if (isAirCall || isCallViaAirCall) return 'Copy contact number'
    if (!isCallDisabled && isCallOutboundDisabled && !isVariantCreateContactModal) {
      if (user?.isAdminRole || user?.isOwnerRole || inbox?.ownerId === user?.id) {
        const inboxId = conversation?.inbox_id

        return (
          <>
            Outbound calling is disabled
            <br />
            <button
              onClick={(event) => {
                event.preventDefault()
                const redirectPath = canManageInboxCalling
                  ? `/settings/inboxes/${inboxId}/settings/calling/general`
                  : `/settings/organization/inboxes/all-inboxes/${inboxId}/calling`

                uiStore.changeRouteViaVue({
                  path: redirectPath,
                })
                contactsDetailsGlobalStore.handleChangeCollapse(true)
              }}
            >
              Enable it here
            </button>
          </>
        )
      }

      return 'Outbound calling is disabled. Contact your admin to enable it.'
    }
    if (callerIdFailedReason) {
      return callerIdFailedReason
    }

    return ''
  }

  const handleCallClick = () => {
    if (config?.onActionCall) {
      config.onActionCall()

      return
    }

    handleConversationCall(callStore)
  }

  useEffect(() => {
    if (isDropdownOpen) callStore.deviceSettingsStore.syncDevicesStream()

    return () => callStore.deviceSettingsStore.removeStream()
  }, [isDropdownOpen])

  return (
    <Button
      ref={buttonRef}
      size='medium'
      typeBtn='contained'
      contained='secondary'
      icon='phone'
      iconProps={{
        color: isCallButtonDisabled
          ? 'var(--content-neutral-primary)'
          : 'var(--content-primary-tertiary)',
      }}
      aria-label={'ContactCallButton'}
      disabled={isCallButtonDisabled}
      tooltipProps={{
        placement: 'bottom-start',
        label: getTooltipLabel(),
      }}
      loading={creatingConversation}
      titleContent={titleContent}
      onClick={handleCallClick}
    />
  )
})
